import { useState } from 'react'
import type { AdminParameter, UnmappedChannel } from '../api/types'
import { formatDate } from '../lib/time'

interface Props {
  queue: UnmappedChannel[]
  parameters: AdminParameter[]
  busy: boolean
  /** Opens the parameter form. A channel can only be mapped to a parameter
   *  that already exists, so a genuinely new kind of sensor is a dead end
   *  without this. */
  onDefineParameter: () => void
  onResolve: (id: number, body: {
    action: 'map' | 'dismiss'
    parameter?: string
    event_driven?: boolean
    note?: string
  }) => void
}

/** The queue of channel IDs seen in a payload with no mapping.
 *
 *  Load-bearing, not an edge case: the channel set changes across the
 *  archive, so a backfill surfaces channels a probe of today does not report.
 *  Every row here is data being discarded on every run until someone decides
 *  what it is.
 */
export function UnmappedQueue({ queue, parameters, busy, onDefineParameter,
                                onResolve }: Props) {
  const open = queue.filter((row) => row.resolved_at === null)

  if (!open.length) {
    return <p className="muted">Nothing unresolved.</p>
  }

  return (
    <div className="queue">
      {open.map((row) => (
        <QueueRow key={row.id} row={row} parameters={parameters} busy={busy}
                  onDefineParameter={onDefineParameter}
                  onResolve={onResolve} />
      ))}
    </div>
  )
}

function QueueRow({ row, parameters, busy, onDefineParameter, onResolve }: {
  row: UnmappedChannel
  parameters: AdminParameter[]
  busy: boolean
  onDefineParameter: () => void
  onResolve: Props['onResolve']
}) {
  const [parameter, setParameter] = useState('')
  const [eventDriven, setEventDriven] = useState(false)
  const [note, setNote] = useState('')

  return (
    <article className="queue-row">
      <div className="queue-head">
        <div>
          <strong>{row.station}</strong> channel <code>{row.channel_id}</code>
          {row.source_name && <> — “{row.source_name}”</>}
          {row.source_unit && row.source_unit !== '-' && (
            <span className="muted"> ({row.source_unit})</span>
          )}
        </div>
        {row.window_from && row.window_to && (
          <span className="muted">
            seen {formatDate(row.window_from)} → {formatDate(row.window_to)}
          </span>
        )}
      </div>

      <div className="queue-actions">
        <select value={parameter} onChange={(e) => {
          if (e.target.value === '__new__') onDefineParameter()
          else setParameter(e.target.value)
        }}>
          <option value="">Choose a parameter…</option>
          {parameters.map((p) => (
            <option key={p.code} value={p.code}>
              {p.display_name} ({p.unit})
            </option>
          ))}
          {/* Without this a channel measuring something the network has never
              measured is a dead end: the list is all there is to map to. */}
          <option value="__new__">＋ Define a new parameter…</option>
        </select>

        <label className="check">
          <input type="checkbox" checked={eventDriven}
                 onChange={(e) => setEventDriven(e.target.checked)} />
          <span title="Absence is expected rather than missing data, a
                       tipping bucket, not a scheduled reading.">
            Event-driven
          </span>
        </label>

        <input className="grow" placeholder="Note for the audit log"
               value={note} onChange={(e) => setNote(e.target.value)} />

        <button className="button primary" disabled={!parameter || busy}
                onClick={() => onResolve(row.id, {
                  action: 'map', parameter, event_driven: eventDriven,
                  note: note || undefined,
                })}>
          Map
        </button>
        {/* Dismissal is for a channel that is genuinely nothing — an MRL
            default name on an empty slot. It re-queues if it ever reports
            again, so it is not a permanent decision. */}
        <button className="button" disabled={busy}
                onClick={() => onResolve(row.id, {
                  action: 'dismiss', note: note || undefined,
                })}>
          Dismiss
        </button>
      </div>
    </article>
  )
}
